/**
 * Models Page
 *
 * Associate HuggingFace models with project versions.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, Modal, useTheme, spacing } from '@fti/design-system';
import { useModels, useModelMutations } from '../../api/models';
import { useHierarchy } from '../../context/HierarchyContext';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import { ConfirmModal } from '../shared/ConfirmModal';
import type { ModelAssociation } from '../../api/types';
import { RoleGate } from '../shared/RoleGate';
import { GATES } from '../../auth/roles';
import { errorMessage } from '../../api/errors';

function ModelFormModal({ isOpen, onClose, onSave, initialName, initialMaxTokens, initialResultTable, title }: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, maxTokens: number, resultTable: string) => Promise<void>;
  initialName: string;
  initialMaxTokens: string;
  initialResultTable: string;
  title: string;
}) {
  const { theme } = useTheme();
  const [name, setName] = useState(initialName);
  const [maxTokens, setMaxTokens] = useState(initialMaxTokens);
  const [resultTable, setResultTable] = useState(initialResultTable);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    background: theme.colors.background.paper,
    color: textColor,
    fontSize: '0.875rem',
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setErrorMsg('');
    try {
      await onSave(name.trim(), Number(maxTokens) || 512, resultTable);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header><h3 style={{ margin: 0, color: textColor }}>{title}</h3></Modal.Header>
      <Modal.Body>
        <Stack gap="sm">
          <div>
            <label style={{ fontSize: '0.8rem', color: mutedColor, display: 'block', marginBottom: '4px' }}>HuggingFace Model Name</label>
            <input placeholder="e.g. distilbert-base-uncased" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: mutedColor, display: 'block', marginBottom: '4px' }}>Max Token Limit</label>
            <input type="number" value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: mutedColor, display: 'block', marginBottom: '4px' }}>Result Table Type</label>
            <select value={resultTable} onChange={(e) => setResultTable(e.target.value)} style={inputStyle}>
              <option value="Classification">Classification</option>
            </select>
          </div>
          {errorMsg && <span style={{ color: theme.colors.semantic.error.default, fontSize: '0.75rem' }}>{errorMsg}</span>}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Flex gap="sm" justify="end">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="solid" size="sm" onClick={handleSave} disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
}

export function ModelsPage() {
  const { theme } = useTheme();
  const { selectedProject } = useHierarchy();
  const { data: allModels, loading, error, refetch } = useModels({ limit: 100 });
  const { createModel, updateModel, deleteModel } = useModelMutations();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingModel, setEditingModel] = useState<ModelAssociation | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ModelAssociation | null>(null);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  // Filter models by selected project
  const models = selectedProject
    ? allModels?.filter((m) => m.id_project === selectedProject.id)
    : allModels;

  const handleSaveNew = async (name: string, maxTokens: number, resultTable: string) => {
    if (!selectedProject) return;
    await createModel({
      name,
      id_project: selectedProject.id,
      active: true,
      result_table_name: resultTable,
      max_token_limit: maxTokens,
    });
    setShowAddModal(false);
    refetch();
  };

  const handleSaveEdit = async (name: string, maxTokens: number, resultTable: string) => {
    if (!editingModel) return;
    await updateModel(editingModel.id, {
      name,
      max_token_limit: maxTokens,
      result_table_name: resultTable,
    });
    setEditingModel(null);
    refetch();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteModel(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    } catch (err: unknown) {
      // Previously `catch { /* error handled */ }` — it was not. A failed delete
      // left the confirmation modal open with no feedback at all.
      setDeleteErrorMsg(errorMessage(err, 'Failed to delete model.'));
    }
  };

  if (!selectedProject) {
    return (
      <Stack gap="md">
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600, color: textColor }}>Models</h2>
        <Card variant="outlined" padding="lg">
          <Flex direction="column" align="center">
            <span style={{ color: mutedColor }}>
              Select a client, matter, and project from the context bar to manage model associations.
            </span>
          </Flex>
        </Card>
      </Stack>
    );
  }

  if (loading) return <LoadingState message="Loading models..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  return (
    <Stack gap="md">
      <Flex justify="between" align="center">
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600, color: textColor }}>Models</h2>
          <p style={{ margin: `${spacing.xs} 0 0`, color: mutedColor, fontSize: '0.85rem' }}>
            Project: {selectedProject.name} (v{selectedProject.version})
          </p>
        </div>
        <RoleGate minRole={GATES.hierarchyWrite}>
          <Button variant="solid" size="sm" onClick={() => setShowAddModal(true)}>+ Associate Model</Button>
        </RoleGate>
      </Flex>

      {models && models.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {models.map((model) => (
            <Card key={model.id} variant="outlined" padding="sm">
              <Flex justify="between" align="center">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: textColor, fontSize: '0.95rem' }}>
                    {model.name}
                  </div>
                  <Flex gap="sm" style={{ marginTop: '2px' }}>
                    <span style={{ color: mutedColor, fontSize: '0.8rem' }}>
                      Max tokens: {model.max_token_limit}
                    </span>
                    <span style={{ color: mutedColor, fontSize: '0.8rem' }}>
                      Result: {model.result_table_name}
                    </span>
                    <span style={{
                      fontSize: '0.75rem',
                      padding: '1px 6px',
                      borderRadius: '3px',
                      background: model.active ? theme.colors.semantic.success.light + '30' : theme.colors.background.paper,
                      color: model.active ? theme.colors.semantic.success.default : mutedColor,
                    }}>
                      {model.active ? 'Active' : 'Inactive'}
                    </span>
                  </Flex>
                </div>
                <Flex gap="xs">
                  <RoleGate minRole={GATES.hierarchyWrite}>
                    <Button variant="ghost" size="sm" onClick={() => setEditingModel(model)}>Edit</Button>
                    <Button variant="ghost" size="sm" color="error" onClick={() => setDeleteTarget(model)}>Remove</Button>
                  </RoleGate>
                </Flex>
              </Flex>
            </Card>
          ))}
        </div>
      ) : (
        <Card variant="outlined" padding="lg">
          <Flex direction="column" align="center">
            <span style={{ color: mutedColor }}>No models associated with this project</span>
          </Flex>
        </Card>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <ModelFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveNew}
          initialName=""
          initialMaxTokens="512"
          initialResultTable="Classification"
          title="Associate Model"
        />
      )}

      {/* Edit Modal */}
      {editingModel && (
        <ModelFormModal
          isOpen={!!editingModel}
          onClose={() => setEditingModel(null)}
          onSave={handleSaveEdit}
          initialName={editingModel.name}
          initialMaxTokens={String(editingModel.max_token_limit)}
          initialResultTable={editingModel.result_table_name}
          title="Edit Model"
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Remove Model"
        message={deleteErrorMsg || `Remove "${deleteTarget?.name}" from this project?`}
        confirmLabel={deleteErrorMsg ? 'OK' : 'Remove'}
        variant={deleteErrorMsg ? 'warning' : 'danger'}
        onConfirm={deleteErrorMsg ? () => { setDeleteTarget(null); setDeleteErrorMsg(''); } : handleDeleteConfirm}
        onCancel={() => { setDeleteTarget(null); setDeleteErrorMsg(''); }}
      />
    </Stack>
  );
}
